// Version mobile responsive pour l'interface Élève - Explications
import React, { useState, useEffect } from "react";
import {
  PlusCircleIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import {
  ExplanationRequest,
  ExplanationStatus,
  CreateRequestInput,
  TimeSlot,
} from "../../../types/explanations";
import {
  useRequestsApi,
  type RequestResponse,
} from "../../../hooks/useRequestsApi";
import RequestForm from "./RequestForm";
import MyRequests from "./MyRequests";
import SlotSelectorModal from "./SlotSelectorModal";
import RequestDetailsModal from "./RequestDetailsModal";
import MeetLinkDisplay from "../../common/MeetLinkDisplay";

// Composants mobiles
import MobileCard from "../../common/MobileComponents/MobileCard";
import MobileButton from "../../common/MobileComponents/MobileButton";
import MobileBadge from "../../common/MobileComponents/MobileBadge";
import MobileTabs from "../../common/MobileComponents/MobileTabs";
import MobileStatsCard from "../../common/MobileComponents/MobileStatsCard";

const MobileStudentExplanations: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"new" | "my-requests">("new");
  const [requests, setRequests] = useState<RequestResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSlotSelector, setShowSlotSelector] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRequest, setSelectedRequest] =
    useState<RequestResponse | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Utiliser le nouveau hook pour l'API
  const { getMyRequests, createRequest, confirmSlot } = useRequestsApi();

  // Charger les demandes de l'élève
  const fetchMyRequests = async () => {
    try {
      setLoading(true);
      const response = await getMyRequests();

      if (response.status === 200 && Array.isArray(response.body)) {
        setRequests(response.body);

        // Compter les demandes non lues (statut changé)
        const unread = response.body.filter(
          (req) =>
            req.status === "slots_proposed" || req.status === "validated",
        ).length;
        setUnreadCount(unread);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des demandes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyRequests();
  }, []);

  // Soumettre une nouvelle demande
  const handleSubmitRequest = async (data: CreateRequestInput) => {
    try {
      const response = await createRequest(data);
      console.log("📨 Réponse createRequest:", response);

      // Vérifier si la réponse est un succès (200-299)
      if (response.status >= 200 && response.status < 300) {
        // Vérifier si le body contient un message d'erreur
        if (
          response.body &&
          typeof response.body === "object" &&
          "message" in response.body &&
          !("_id" in response.body)
        ) {
          toast.error(
            (response.body.message as string) || "Erreur lors de la création",
          );
          return;
        }

        // Succès
        toast.success(
          "Demande créée avec succès ! Vous recevrez un email de confirmation.",
        );
        fetchMyRequests(); // Recharger la liste
        setActiveTab("my-requests"); // Basculer vers l'onglet des demandes
      } else {
        const errorMessage =
          response.body &&
          typeof response.body === "object" &&
          "message" in response.body
            ? (response.body.message as string)
            : "Erreur lors de la création de la demande";
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error("❌ Exception:", error);
      toast.error(error?.message || "Erreur lors de la création de la demande");
    }
  };

  // Sélectionner un créneau
  const handleSelectSlot = (request: RequestResponse | any) => {
    setSelectedRequest(request);
    setShowSlotSelector(true);
  };

  // Ouvrir le modal de détails
  const handleViewDetails = (request: RequestResponse | any) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  // Confirmer la sélection de créneau
  const handleSlotSelection = async (slotId: string) => {
    if (!selectedRequest) return;

    try {
      const response = await confirmSlot(selectedRequest._id, slotId);
      console.log("📨 Réponse confirmSlot:", response);

      // Vérifier si la réponse est un succès (200-299)
      if (response.status >= 200 && response.status < 300) {
        // Succès
        toast.success("Créneau confirmé avec succès !");
        setShowSlotSelector(false);
        setSelectedRequest(null);
        fetchMyRequests(); // Recharger la liste
      } else {
        const errorMessage =
          response.body &&
          typeof response.body === "object" &&
          "message" in response.body
            ? (response.body.message as string)
            : "Erreur lors de la sélection du créneau";
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error("❌ Exception:", error);
      toast.error(error?.message || "Erreur lors de la sélection du créneau");
    }
  };

  // Statistiques rapides
  const stats = {
    total: requests.length,
    pending: requests.filter((req) => req.status === "pending").length,
    scheduled: requests.filter((req) => req.status === "scheduled").length,
    completed: requests.filter((req) => req.status === "completed").length,
  };

  // Fonctions de statut
  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "validated":
        return "bg-blue-100 text-blue-800";
      case "slots_proposed":
        return "bg-purple-100 text-purple-800";
      case "student_confirmed":
        return "bg-indigo-100 text-indigo-800";
      case "meeting_scheduled":
        return "bg-green-100 text-green-800";
      case "scheduled":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "completed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "pending":
        return "En attente";
      case "validated":
        return "Validée";
      case "slots_proposed":
        return "Créneaux proposés";
      case "student_confirmed":
        return "Créneau confirmé";
      case "meeting_scheduled":
        return "Réunion programmée";
      case "scheduled":
        return "Programmée";
      case "rejected":
        return "Rejetée";
      case "completed":
        return "Terminée";
      default:
        return status;
    }
  };

  // Configuration des onglets
  const tabs = [
    {
      id: "new",
      label: "Nouvelle demande",
      icon: <PlusCircleIcon className="w-4 h-4" />,
    },
    {
      id: "my-requests",
      label: "Mes demandes",
      count: requests.length,
      icon: <DocumentTextIcon className="w-4 h-4" />,
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header mobile */}
      <div className="bg-white border-b border-stroke sticky top-0 z-10">
        <div className="px-4 py-4">
          <h1 className="text-xl font-bold text-gray-900">
            Explications d'exercices
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Demandez une explication personnalisée
          </p>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Statistiques rapides */}
        <div className="grid grid-cols-2 gap-3">
          <MobileStatsCard
            title="Total demandes"
            value={stats.total}
            icon={<DocumentTextIcon />}
            color="primary"
          />
          <MobileStatsCard
            title="En attente"
            value={stats.pending}
            icon={<ClockIcon />}
            color="warning"
          />
          <MobileStatsCard
            title="Programmées"
            value={stats.scheduled}
            icon={<CalendarIcon />}
            color="info"
          />
          <MobileStatsCard
            title="Terminées"
            value={stats.completed}
            icon={<CheckCircleIcon />}
            color="success"
          />
        </div>

        {/* Navigation par onglets */}
        <MobileTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={(tabId: string) =>
            setActiveTab(tabId as "new" | "my-requests")
          }
        />

        {/* Contenu des onglets */}
        {activeTab === "new" && (
          <MobileCard>
            <RequestForm onSubmit={handleSubmitRequest} />
          </MobileCard>
        )}

        {activeTab === "my-requests" && (
          <MobileCard>
            <MyRequests
              requests={requests}
              loading={loading}
              onSelectSlot={handleSelectSlot}
              onViewDetails={handleViewDetails}
              getStatusBadgeColor={getStatusBadgeColor}
              getStatusText={getStatusText}
            />
          </MobileCard>
        )}

        {/* Modale de sélection de créneaux */}
        {showSlotSelector && selectedRequest && (
          <SlotSelectorModal
            request={selectedRequest}
            onConfirm={handleSlotSelection}
            onClose={() => {
              setShowSlotSelector(false);
              setSelectedRequest(null);
            }}
          />
        )}

        {/* Modal de détails */}
        {showDetailsModal && selectedRequest && (
          <RequestDetailsModal
            open={showDetailsModal}
            request={selectedRequest}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedRequest(null);
            }}
            getStatusBadgeColor={getStatusBadgeColor}
            getStatusText={getStatusText}
          />
        )}
      </div>
    </div>
  );
};

export default MobileStudentExplanations;
